import { z } from 'zod';

/**
 * Locator Strategy Overhaul · Phase 2 (2026-06-08) — `ElementFingerprint`.
 *
 * The full structural + semantic fingerprint of a captured DOM element.
 * Recorded at Build-Mode / Recorder commit time and persisted onto the
 * step alongside the existing `target` / `targetMeta` / `recordedCandidates`
 * fields. At run time the resolver consults this fingerprint as a
 * *signal-rich fallback*: when every selector strategy misses (the page
 * was refactored, the testid was renamed, the role-name pair changed),
 * the fingerprint-scored resolver walks the live DOM, scores each
 * candidate against the recorded fingerprint, and picks the best match
 * above a minimum confidence.
 *
 * Why a fingerprint and not just more selectors:
 *   - Selectors are brittle. A team renames a class, a designer changes
 *     a button's accessible name, an A/B test inserts a wrapper — the
 *     selector fails, the step fails. The fingerprint captures the
 *     element's *identity* (role, accessible name, attributes, parent
 *     chain, position) rather than any single way to address it. A
 *     refactored page where the element keeps its semantics still
 *     matches.
 *   - Market leaders (Mabl, Testim, Functionize) all do this. Rynora
 *     today only has `recordedCandidates` (a list of selectors) so a
 *     correlated selector failure ripples through every candidate.
 *
 * Wire shape is intentionally small (target ≤ ~3KB per element) so we
 * can store one per step without bloating the flow JSON. The
 * `outerHTML` hash is a fixed 64-char SHA-256 of the captured element's
 * outerHTML rather than the HTML itself.
 *
 * Forward-compat:
 *   - Phase 3 will extend this with `computedAxRole` + `computedAxName`
 *     captured via CDP `Accessibility.getFullAXTree`.
 *   - Phase 4 will extend this with `screenshotPHash` + `bboxAtCapture`
 *     for visual confirmation.
 *
 * All fields are optional except `outerHTMLHash` because older clients
 * may capture partial fingerprints; the resolver scores what's present
 * and drops fingerprint matching entirely when nothing useful is set.
 */

/**
 * One ancestor in the captured element's parent chain. Walked
 * top-down (deepest parent first, root last) so a fingerprint match
 * can short-circuit on the first ancestor that disagrees.
 *
 * Capped at 5 entries by the recorder — that's enough to disambiguate
 * sibling rows in a virtualised list while staying well under 1 KB
 * per fingerprint.
 */
export const FingerprintAncestorSchema = z.object({
  tag: z.string().min(1).max(64),
  /** Computed ARIA role (`button`, `link`, `option`, …). May be empty
   *  for a generic `<div>` wrapper. */
  role: z.string().max(64).optional(),
  /** Accessible name from `aria-label` / `<label>` / inner text. */
  accName: z.string().max(120).optional(),
  /** Indexed test-id family (`data-testid` / `data-test-id` / `data-cy`)
   *  for the highest-signal anchors. */
  testId: z.string().max(120).optional(),
  /** HTML `id` attribute, when stable (recorder filters
   *  framework-generated ids — `react-…`, `mui-…`, etc.). */
  id: z.string().max(120).optional(),
  /** Stable class subset (recorder strips state-tracking classes
   *  like `active`, `open`, framework prefixes). At most 3 entries. */
  classList: z.array(z.string().min(1).max(80)).max(3).optional(),
});
export type FingerprintAncestor = z.infer<typeof FingerprintAncestorSchema>;

/**
 * Visible-bounding-box at capture time. Used by Phase 4's visual
 * fingerprint and by the scorer's "elements should be roughly the
 * same size as last time" heuristic. Coordinates are page-relative
 * (not viewport-relative) so a re-scroll between record and run
 * doesn't trip the match.
 */
export const FingerprintBBoxSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().finite().min(0),
  height: z.number().finite().min(0),
});
export type FingerprintBBox = z.infer<typeof FingerprintBBoxSchema>;

/**
 * Sibling context for "this is the 3rd of 5 product cards" cases.
 * The scorer uses position (siblingIndex / siblingCount) as a weak
 * tiebreaker between otherwise-identical candidates — useful for
 * lists of cards with the same role + name pattern (e.g. checkout
 * line items, search-result tiles).
 */
export const FingerprintSiblingContextSchema = z.object({
  /** Same-tag sibling index of the element (0-based). */
  siblingIndex: z.number().int().nonnegative().max(1000),
  /** Total same-tag siblings under the same parent at capture
   *  time. The scorer downweights position when this count drifts. */
  siblingCount: z.number().int().positive().max(1000),
  /** Visible text snippets from the IMMEDIATELY preceding and
   *  following siblings (capped at 60 chars each). Used by the
   *  scorer to anchor an otherwise-ambiguous element. */
  prevSiblingText: z.string().max(60).optional(),
  nextSiblingText: z.string().max(60).optional(),
});
export type FingerprintSiblingContext = z.infer<typeof FingerprintSiblingContextSchema>;

/**
 * Full element fingerprint persisted on the step. See file header for
 * the full rationale; in short: this is what the resolver uses when
 * every selector misses.
 */
export const ElementFingerprintSchema = z.object({
  /**
   * Schema version. Bumps when the scorer's weighting changes in a way
   * that's not backwards-compatible (a v1 fingerprint can still be
   * scored by a v2 resolver, but the resolver may downweight v1 if
   * the v2 layout assumes new fields are present).
   */
  version: z.literal(1),
  /** Lowercase tag name of the captured element. */
  tag: z.string().min(1).max(64),
  /** Computed ARIA role (recorder reads `el.getAttribute('role')`
   *  first, falls back to implicit roles for `button` / `a` /
   *  `input` / `select` / `textarea`). */
  role: z.string().max(64).optional(),
  /** Accessible name (`aria-label` → `<label for>` → inner text).
   *  Capped to 120 chars to match the runner's `pickedText` shape. */
  accName: z.string().max(120).optional(),
  /**
   * Locator Strategy Overhaul · Phase 3.3 (2026-06-08) — CDP-derived
   * role.
   *
   * The DOM-side `role` field above is what the recorder INFERS from
   * the element's attributes + tag (`el.getAttribute('role')` →
   * implicit). `computedAxRole` is what the BROWSER itself reports
   * via CDP `Accessibility.getFullAXTree`. They usually agree, but
   * diverge in W3C-compliant ways for:
   *   - elements whose role is computed via `aria-owns` (the parent
   *     in the DOM says nothing about role; only the accessibility
   *     tree captures it correctly).
   *   - elements inside shadow DOM where the role is contributed by
   *     a host-defined `aria-*` attribute on the shadow root.
   *   - elements where `aria-labelledby` overrides the recorder's
   *     visible-text guess.
   *
   * The scorer treats this as a HIGHER-CONFIDENCE signal than `role`
   * because it reflects the browser's own accessibility computation
   * — it's what screen readers see, and what `getByRole` would
   * resolve to if Playwright still had a full CDP-backed role
   * engine. Optional + back-compat: old fingerprints captured before
   * Phase 3 ship the DOM-side `role` only and the scorer falls
   * through gracefully.
   */
  computedAxRole: z.string().max(64).optional(),
  /**
   * Locator Strategy Overhaul · Phase 3.3 (2026-06-08) — CDP-derived
   * accessible name.
   *
   * Counterpart to `computedAxRole`. The DOM-side `accName` is the
   * recorder's best guess from `aria-label` / `<label for>` / inner
   * text. `computedAxName` is the BROWSER's computed accessible
   * name per the W3C accname algorithm — `aria-labelledby` chains
   * resolved, `aria-label` precedence applied, hidden-text exclusions
   * honoured. The scorer treats matches on this field at the same
   * weight as `accName` but breaks ties in favour of `computedAxName`
   * when both fields are populated.
   */
  computedAxName: z.string().max(200).optional(),
  /** Selected attributes (`type`, `name`, `placeholder`, `href`,
   *  `for`, `title`, `alt`, `data-testid` family, `role`,
   *  `aria-*`). Keys are lowercased; values capped at 120 chars
   *  individually and 2 KB in aggregate. The resolver scores keyed
   *  attributes higher than positional ones. */
  attrs: z.record(z.string().max(120)).optional(),
  /** Ancestor chain top-down (deepest parent first). Capped at 5
   *  entries to keep the wire payload bounded. */
  parents: z.array(FingerprintAncestorSchema).max(5).optional(),
  /** Element's visible bounding box at capture time. */
  bbox: FingerprintBBoxSchema.optional(),
  /** Sibling-position context (see `FingerprintSiblingContext`). */
  siblings: FingerprintSiblingContextSchema.optional(),
  /**
   * SHA-256 hex digest of the element's `outerHTML` at capture
   * time. Used by the scorer as a high-confidence "exact match"
   * signal: when a candidate's outerHTML hashes to the same value
   * the element is the same one we captured (modulo dynamic
   * attributes the recorder normalised out before hashing — see
   * `__rynoraOuterHtmlHash` in `dom-scripts.ts`).
   *
   * REQUIRED so an old client that captures a partial fingerprint
   * still always has at least the hash to score against.
   */
  outerHTMLHash: z.string().length(64),
  /**
   * Locator Strategy Overhaul · Phase 4.1 (2026-06-08) — perceptual
   * hash of an element-bbox-cropped screenshot, captured server-side
   * at record / Build-Mode-commit time.
   *
   * Encoded as a 16-character hex string (64-bit difference hash).
   * The dHash algorithm:
   *   1. Crop the page screenshot to the captured `bbox` (with 10px
   *      padding to absorb sub-pixel layout drift).
   *   2. Greyscale + downscale to 9x8 via nearest-neighbour sampling.
   *   3. For each of the 8 rows, emit 8 bits comparing adjacent pixel
   *      pairs (pixel[i] > pixel[i+1] → 1, else 0).
   *   4. Pack the 64 bits as a hex string.
   *
   * Two elements with similar appearance produce hashes whose
   * Hamming distance is small. The fingerprint-scored resolver uses
   * `screenshotPHash` as a TIE-BREAKER between candidates that
   * otherwise score equally — Mabl's "advanced auto-heal" pattern.
   *
   * Optional + back-compat: older fingerprints don't have it, and
   * the scorer simply skips that facet. We don't make it required
   * because screenshot capture can legitimately fail (headless
   * rendering quirks, element off-screen at record time, CDP
   * busy) without invalidating the rest of the fingerprint.
   */
  screenshotPHash: z.string().length(16).optional(),
  /**
   * ISO-8601 capture timestamp. The resolver may downweight very
   * stale fingerprints (>30 days) to nudge users towards re-recording
   * before deep healing burns budget on a likely-misaligned identity.
   */
  capturedAt: z.string().min(20).max(40),
});
export type ElementFingerprint = z.infer<typeof ElementFingerprintSchema>;

/**
 * One scored candidate returned by the fingerprint-scored resolver
 * mode. Surfaced in failure telemetry + the run-result chip so
 * operators can audit why a particular DOM node matched.
 */
export const FingerprintScoredCandidateSchema = z.object({
  /** Stable selector the resolver synthesised for the candidate
   *  (the cheapest representation: testid > id > role+name > xpath). */
  selector: z.string().min(1).max(4096),
  /** 0-1 score from the weighted scorer. Threshold for an automatic
   *  match is 0.65; below that, the resolver advances to deep
   *  healing or surfaces an ambiguity for Phase 4's LLM tie-breaker. */
  score: z.number().min(0).max(1),
  /** Per-facet sub-scores so operators can see WHY this candidate
   *  scored where it did. Sums (weighted) into `score`. */
  facets: z.object({
    tag: z.number().min(0).max(1).optional(),
    role: z.number().min(0).max(1).optional(),
    accName: z.number().min(0).max(1).optional(),
    attrs: z.number().min(0).max(1).optional(),
    parents: z.number().min(0).max(1).optional(),
    siblings: z.number().min(0).max(1).optional(),
    bbox: z.number().min(0).max(1).optional(),
    outerHTMLHash: z.number().min(0).max(1).optional(),
    /** Phase 4.1 — Hamming-similarity score for the screenshot
     *  pHash (1 = identical, 0 = maximally different). Skipped
     *  when either side lacks a recorded pHash. */
    screenshotPHash: z.number().min(0).max(1).optional(),
  }),
});
export type FingerprintScoredCandidate = z.infer<typeof FingerprintScoredCandidateSchema>;
